import { computed, inject, Injectable, resource, ResourceRef } from '@angular/core';
import { SonaAuthApiFacade } from '@sona/angular-auth-api';
import { mutation, useI18n } from '@sona/angular-util-common';
import { Organization, OrganizationId, OrganizationMemberRole, OrganizationMemberWithDetails, OrganizationSubscriptionInfo } from '@sona/shared-model-common';
import { toast } from 'ngx-sonner';
import { firstValueFrom } from 'rxjs';

import { OrganizationApiService } from '../../infrastructure/services/organization-api.service';

@Injectable({
    providedIn: 'root'
})
export class OrganizationFacade {
    private readonly _authFacade = inject(SonaAuthApiFacade);
    private readonly _organizationApi = inject(OrganizationApiService);
    private readonly _i18n = useI18n();

    readonly currentOrganizationSubscriptionResource: ResourceRef<OrganizationSubscriptionInfo> = resource({
        request: () => ({ organizationId: this._authFacade.organizationId() }),
        loader: async ({ request }) => {
            if (!request.organizationId) {
                throw new Error('No organization selected');
            }
            try {
                return await firstValueFrom(this._organizationApi.getOrganizationSubscription(request.organizationId));
            } catch (error) {
                console.error('Failed to load organization subscription:', error);
                toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.SUBSCRIPTION.LOAD.ERROR'));
                throw error;
            }
        }
    });

    private readonly _upgradeSubscriptionMutation = mutation({
        loader: ({ organizationId, planName }: {
            organizationId: OrganizationId;
            planName: string
        }) => firstValueFrom(this._organizationApi.upgradeSubscription(organizationId, planName)),
        onError: () => toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.SUBSCRIPTION.UPGRADE_LINK.ERROR')),
        onSuccess: () => {
            this.currentOrganizationSubscriptionResource.reload();
        }
    });

    private readonly _updateOrganizationMutation = mutation<Organization, { organizationId: OrganizationId; name: string }>({
        loader: ({ organizationId, name }) => firstValueFrom(this._organizationApi.updateOrganization(organizationId, name)),
        onSuccess: (updatedOrganization) => {
            toast.success(this._i18n().t('ORGANIZATION.FEEDBACK.UPDATE.SUCCESS'));
            this._authFacade.organization.set(updatedOrganization);
        },
        onError: (error) => {
            console.error('Failed to update organization:', error);
            toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.UPDATE.ERROR'));
        }
    });

    private readonly _inviteUsersMutation = mutation<unknown, { organizationId: OrganizationId; emails: string[] }>({
        loader: ({ organizationId, emails }) => firstValueFrom(this._organizationApi.inviteUsersToOrganization(organizationId, emails)),
        onError: (error) => {
            console.error('Failed to send invitation:', error);
            toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.INVITE.ERROR'));
        }
    });

    private readonly _removeUserMutation = mutation<void, { organizationId: OrganizationId; userId: string }>({
        loader: ({ organizationId, userId }) => firstValueFrom(this._organizationApi.removeUserFromOrganization(organizationId, userId)),
        onSuccess: () => {
            toast.success(this._i18n().t('ORGANIZATION.FEEDBACK.REMOVE.SUCCESS'));
        },
        onError: (error) => {
            console.error('Failed to remove user:', error);
            toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.REMOVE.ERROR'));
        }
    });

    private readonly _customerPortalMutation = mutation<{ url: string }, { organizationId: OrganizationId }>({
        loader: ({ organizationId }) => firstValueFrom(this._organizationApi.getStripeCustomerPortalUrl(organizationId)),
        onError: (error) => {
            console.error('Failed to get customer portal URL:', error);
            toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.BILLING_PORTAL.ERROR'));
        }
    });

    // Status signals derived from mutations
    readonly isInviting = this._inviteUsersMutation.isMutating;
    readonly isRemoving = this._removeUserMutation.isMutating;
    readonly isUpdating = this._updateOrganizationMutation.isMutating;
    readonly isBillingAction = this._customerPortalMutation.isMutating;

    readonly currentOrganization = computed(() => this._authFacade.organization.value());
    readonly currentOrganizationSubscription = computed(() => this.currentOrganizationSubscriptionResource.value());

    // Permission-based computed values
    readonly currentUserRole = computed<OrganizationMemberRole | null>(() => {
        const currentUser = this._authFacade.user();
        const currentOrganization = this.currentOrganization();

        if (!currentUser || !currentOrganization) {
            return null;
        }

        if (currentOrganization.ownerId === currentUser.id) {
            return 'owner';
        }

        return 'member';
    });
    readonly canManageOrganization = computed(() => this.currentUserRole() === 'owner');
    readonly canInviteMembers = computed(() => this.currentUserRole() === 'owner');
    readonly canRemoveMembers = computed(() => this.currentUserRole() === 'owner');
    readonly canManageBilling = computed(() => this.currentUserRole() === 'owner');

    getOrganizationMembers(): ResourceRef<OrganizationMemberWithDetails[]> {
        const hasAccess = this._authFacade.hasPermission('multi_user');

        return resource({
            request: () => ({ organizationId: this._authFacade.organizationId(), hasAccess: hasAccess() }),
            loader: async ({ request }) => {
                if (!request.hasAccess || !request.organizationId) {
                    return [];
                }

                try {
                    return await firstValueFrom(this._organizationApi.getOrganizationMembers(request.organizationId));
                } catch (error) {
                    console.error('Failed to load organization members:', error);
                    toast.error(this._i18n().t('ORGANIZATION.FEEDBACK.MEMBERS.LOAD.ERROR'));
                    return [];
                }
            }
        })
    }

    /**
     * Update the current organization
     */
    async updateCurrentOrganization(name: string): Promise<Organization | null> {
        const organizationId = this._authFacade.organizationId();
        if (!organizationId) return null;

        try {
            const updatedOrganization = await this._updateOrganizationMutation.mutate({ organizationId, name });
            return updatedOrganization;
        } catch {
            return null;
        }
    }

    /**
     * Invite a user to the current organization
     */
    async inviteUserToCurrentOrganization(emails: string[]): Promise<boolean> {
        const organizationId = this._authFacade.organizationId();
        if (!organizationId) return false;

        try {
            await this._inviteUsersMutation.mutate({ organizationId, emails });
            toast.success(this._i18n().t('ORGANIZATION.FEEDBACK.INVITE.SUCCESS', { emails: emails.join(', ') }));
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Remove a user from the current organization
     */
    async removeUserFromCurrentOrganization(userId: string): Promise<boolean> {
        const organizationId = this._authFacade.organizationId();
        if (!organizationId) return false;

        try {
            await this._removeUserMutation.mutate({ organizationId, userId });
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Get Stripe customer portal URL for billing management
     */
    async getStripeCustomerPortalUrl(): Promise<string | null> {
        const organizationId = this._authFacade.organizationId();
        if (!organizationId) return null;

        try {
            const { url } = await this._customerPortalMutation.mutate({ organizationId });
            return url;
        } catch {
            return null;
        }
    }

    /**
     * Upgrade organization subscription
     */
    async upgradeSubscription(planName: string) {
        const organizationId = this._authFacade.organizationId();

        if (!organizationId) {
            return;
        }

        const { url } = await this._upgradeSubscriptionMutation.mutate({ organizationId, planName });

        if (url) {
            window.open(url, '_blank');
        }
    }
}